/**
 * Reusable Filter Popover Content Component
 * Extracted from TableHeader to eliminate duplication
 */

import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import Switch, { SwitchProps } from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import IconSearch from 'src/assets/images/icons/search-settings.svg';
import { SortButton } from './SortButton';
import type { FilterItem } from './useTableHeaderLogic';

// Styled switch with green for checked and light gray for unchecked
const DecisionSwitch = styled((props: SwitchProps) => <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />)(({ theme }) => ({
  width: 52,
  height: 32,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 4,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#00A651',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#00A651',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 24,
    height: 24,
  },
  '& .MuiSwitch-track': {
    borderRadius: 32 / 2,
    backgroundColor: '#F2F4F7',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

interface FilterPopoverContentProps {
  // Sorting props
  currentSortDirection: string | false;
  sortAscText?: string;
  sortDescText?: string;
  onApplySort: (direction: 'asc' | 'desc') => void;

  // Filter props
  filterItems: FilterItem[];
  filteredItems: FilterItem[];
  selectedValues: string[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onToggleValue: (itemId: any) => void;
  onApplyFilter: () => void;
  isLoading?: boolean; // Loading state for lazy loaded data
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  showFilter?: boolean;

  // Spot shipment filter (only for shipment number column)
  showSpotFilter?: boolean;
  onlyShowSpot?: boolean;
  onToggleSpotFilter?: (checked: boolean) => void;
}

export const FilterPopoverContent: React.FC<FilterPopoverContentProps> = ({
  currentSortDirection,
  sortAscText = 'Sort A to Z',
  sortDescText = 'Sort Z to A',
  onApplySort,
  filterItems,
  filteredItems,
  selectedValues,
  searchTerm,
  onSearchChange,
  onSelectAll,
  onClearAll,
  onToggleValue,
  onApplyFilter,
  isLoading = false,
  onLoadMore,
  isLoadingMore = false,
  hasMore = false,
  showFilter = true,
  showSpotFilter = false,
  onlyShowSpot = false,
  onToggleSpotFilter,
}) => (
  <Box p={1} display='flex' flexDirection='column'>
    <SortButton direction='asc' currentDirection={currentSortDirection} onClick={onApplySort} text={sortAscText} />
    <SortButton direction='desc' currentDirection={currentSortDirection} onClick={onApplySort} text={sortDescText} />

    {showFilter && (
      <>
        <div className='flex items-center justify-between mt-8 px-8 py-16 border-t border-neutral-20'>
          <div className='flex items-center gap-8'>
            <p className='text-12 lg:text-14 font-medium'>Filter Value to Display</p>
          </div>
          <div className='flex items-center gap-10'>
            <button type='button' onClick={onSelectAll} className='text-10 xl:text-12 font-medium text-primary'>
              Select All
            </button>
            <div className='w-1 h-12 bg-neutral-20' />
            <button type='button' onClick={onClearAll} className='text-10 xl:text-12 font-medium text-neutral-60'>
              Clear
            </button>
          </div>
        </div>

        <div className='pt-4 pb-16 px-8 border-b border-neutral-20'>
          <div className='relative'>
            <img src={IconSearch} alt='search' className='absolute left-16 top-1/2 -translate-y-1/2' />
            <input
              type='text'
              placeholder='Search'
              className='w-full pl-48 pr-16 py-12 bg-neutral-10 border border-neutral-20 rounded-8'
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            <button
              type='button'
              className='absolute right-16 top-1/2 -translate-y-1/2 text-neutral-60'
              onClick={() => onSearchChange('')}
              aria-label='Clear Search'
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {showSpotFilter && (
          <div className='flex items-center justify-between px-8 py-12 border-b border-neutral-20'>
            <div className='flex items-center gap-8'>
              <span className='text-12 lg:text-14 font-medium'>Only Show</span>
              <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>
            </div>
            <DecisionSwitch checked={onlyShowSpot} onChange={(e) => onToggleSpotFilter?.(e.target.checked)} />
          </div>
        )}

        {isLoading && (
          <div className='flex items-center justify-center py-12'>
            <CircularProgress size={24} />
          </div>
        )}

        <div
          className='overflow-y-auto py-12 max-h-[200px]'
          onScroll={(event) => {
            if (!hasMore || isLoadingMore || isLoading) return;
            const target = event.currentTarget;
            const hasReachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
            if (hasReachedBottom) {
              onLoadMore?.();
            }
          }}
        >
          {filteredItems.map((item) => (
            <div
              key={`filtered-${item.id}`}
              className='flex items-center py-8 px-10 hover:bg-neutral-10 cursor-pointer w-full'
              role='checkbox'
              aria-checked={selectedValues.includes(item.id)}
              tabIndex={0}
            >
              <div className='flex items-center justify-between gap-12 w-full' role='presentation'>
                <div className='flex items-center gap-8'>
                  <div className='relative flex items-center'>
                    <input
                      type='checkbox'
                      id={`filter-${item.id}`}
                      checked={selectedValues.includes(item.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggleValue(item.id);
                      }}
                      className='h-18 w-18 rounded-4 border-neutral-40 accent-[#00992B] cursor-pointer'
                      style={{ accentColor: '#00992B' }}
                    />
                  </div>
                  <label htmlFor={`filter-${item.id}`} className='ml-12 text-xs cursor-pointer flex items-center gap-10'>
                    {item.label === null || item.label === 'null' || item.label === '' ? '-' : item?.label}
                    {item?.hasSpotShipment && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
                  </label>
                </div>
                {item.count !== undefined && item.count > 0 && <span className='text-xs text-neutral-60'>{item.count}</span>}
              </div>
            </div>
          ))}
          {!isLoading && filteredItems.length === 0 && <div className='flex items-center justify-center py-12 text-xs text-neutral-60'>No values found</div>}
          {isLoadingMore && (
            <div className='flex items-center justify-center py-12'>
              <CircularProgress size={20} />
            </div>
          )}
        </div>

        <div className='-mb-8 -mx-8 p-16 rounded-b-8 bg-neutral-10 border-t border-neutral-20'>
          <Button
            variant='contained'
            fullWidth
            onClick={onApplyFilter}
            disabled={selectedValues.length === 0}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: '#BFBFBF',
                color: '#FFFFFF',
                cursor: 'not-allowed',
                opacity: 0.9,
              },
            }}
          >
            Apply
          </Button>
        </div>
      </>
    )}
  </Box>
);
